import { Box, ButtonBase, useTheme } from "@mui/material";

import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import NightsStayOutlinedIcon from '@mui/icons-material/NightsStayOutlined';
import PublicIcon from '@mui/icons-material/Public';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import SyncIcon from '@mui/icons-material/Sync';
import DataObjectIcon from '@mui/icons-material/DataObject';
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { localeSettings } from "../../../../utils/locales";
import { tokens } from "../../../../theme";
import { setLanguage, setThemeMode } from "../../../../newapi/global/settingsSlice";
import PopoverModal from "../../../theme/Modal/PopoverModal";
import { ActionButton } from "./components/ActionButton";
import CreateSupportTicketActionButton from "./components/CreateSupportTicketActionButton";
import { linkedItemFromOrder } from "../../../../utils/supportTicketLinkedItems";
import { ActionIcon } from "./components/ActionIcon";
import Text from "../../../theme/text/Text";
import ShSwitch from "../../../theme/inputs/ShSwitch";
import MSelectWorkspaces from "../MSelectWorkspaces";
import { general_states } from "../../../../utils/staticEnums";
import Shbutton from "../../../theme/buttons/Shbutton";
import { selectUser, selectUserPermission } from "../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from "../../../../utils/permissions";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { ReactComponent as ReturnPackage } from "../../../../styles/svg/package-return.svg";
import { ReactComponent as Truck } from "../../../../styles/svg/truck.svg";
import { useGetSettingsQuery } from "../../../../newapi/global/generalApiSlice";
import WarehouseIcon from '@mui/icons-material/Warehouse';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import SplitscreenIcon from '@mui/icons-material/Splitscreen';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import { ShCopyButtonWithOwnChildren } from "../../../theme/ShCopy";
import SHQuickAction from "../../../theme/Modal/SHQuickAction";
import { useFindUrlForReturnQuery } from "../../../../newapi/return/returnsSlice";
import FactCheckIcon from '@mui/icons-material/FactCheck';
import RuleIcon from '@mui/icons-material/Rule';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { claimableShipments } from "../MAddClaim";

const AMOrderSpWarehouses = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const colors = tokens(theme.palette.mode);
    const language = useSelector((state) => state.settings.language);
    const workSpace = useSelector((state) => state.settings.workSpace);
    const themeMode = useSelector((state) => state.settings.themeMode);
    const dispatch = useDispatch();
    const selectedEnvironmentType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    const isFulfilment = useSelector((state) => state.settings.environment.isFulfilment);
    const user = useSelector(selectUser);
    const [open, setOpen] = useState(false);

    const hasCreateReturnPermission = useSelector((state) => 
        selectUserPermission(state, [UserPrivileges.RETURN_MANAGE])
    );

    const hasManageShipmentPermission = useSelector((state) => 
        selectUserPermission(state, [UserPrivileges.ECHECK])
    );

    const hasModifyPrivilege = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.OUTBOUND_MANAGE,UserPrivileges.RP_ORDER_MANAGE])
    );

    const hasInvoicePermission = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.INVOICE_MANAGE])
    );

    const hasDropshipSkipCheckPermission = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.ORDER_DROPSHIP_SKIPCHECK])
    );

    const hasSetToShippedPermission = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.OUTBOUND_ORDER_TO_SHIPPED])
    );

    const workSpaceModal = {
        open: open,
        setOpen: setOpen
    }

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }
    
    const toggleColor = () => {
        dispatch(setThemeMode(themeMode === "dark" ? "light" : "dark"));
        document.querySelector('meta[name="theme-color"]').setAttribute("content", `${theme.palette.mode === "dark" ? "#181820": "#ecf0f5"}`);
        document.querySelector('meta[name="msapplication-navbutton-color"]').setAttribute("content", `${theme.palette.mode === "dark" ? "#181820": "#ecf0f5"}`);
    }

    const toggleLanguage = () => {
        if(language == 'nl') {
            dispatch(setLanguage("en"));
        } else {
            dispatch(setLanguage("nl"));
        }
    }
    const { data: eCheckSettings} = useGetSettingsQuery("echeck");
    const overwriteShipmentPopup = eCheckSettings?.find(setting => setting.key === 'echeck_confirm_order_from_sidepanel')?.value === "true";

    // Actually create the shipment: either jump straight into echeck or open the shipment modal.
    const proceedToShipment = () => {
        if(overwriteShipmentPopup){
            const id = props.orderData.id;
            const state = props.orderData?.state ?? "";
            const combinedString = `${id}+${state}`;
            const encoded = btoa(combinedString);
            window.location.href = `/warehouse/echeck?finalizeorder=${encoded}`;
        } else {
            props.shipmentModal.setOpen(true)
        }
    };

    const canEditPickup = (!props.orderData?.webshopcredentials || (props.orderData?.webshopcredentials && props.orderData.webshopcredentials.webshop_type !== "bol" && 
    props.orderData.webshopcredentials.integratorId === null)) && hasModifyPrivilege &&
    ![general_states.SHIPPED, general_states.CANCELLED].includes(props.orderData?.state);

    const canEditPickingType = !props.orderData?.pickFromBulk && hasModifyPrivilege &&
    [general_states.REPLENISH, general_states.BACKORDERED].includes(props.orderData?.state);

    // A claim needs a shipped order on a shipper that accepts them — the shipper's claim mailbox
    // is what the claim is actually sent to.
    const canCreateClaim = hasModifyPrivilege && props.orderData?.state === general_states.SHIPPED &&
    claimableShipments(props.orderData).some(shipment => shipment.shipper?.canCreateClaim);

    // Super admins can inspect the raw order as the webshop returns it; integrator and api
    // orders have no webshop to fetch from.
    const canViewWebshopJson = user?.sha && props.webshopJsonModal && props.orderData?.externalId &&
    props.orderData?.webshopcredentials && !props.orderData.webshopcredentials.integratorId &&
    props.orderData.webshopcredentials.webshop_type !== "api";

    return (
        <PopoverModal
            {...props}
        >
            <Box 
                sx={{
                    background: theme.palette.mode === "dark" ? colors.grey[100] : "#fff",
                    padding:1.5,
                    paddingBottom: isStandAlone ? 4 : 1.5,
                    display:"flex",
                    gap:1,
                    flexDirection:"column",
                    borderRadius:"24px",
                    borderBottomLeftRadius:0,
                    borderBottomRightRadius:0,
                    "@media screen and (max-width: 48em)" : {
                        borderRadius:"32px",
                        borderBottomLeftRadius:0,
                        borderBottomRightRadius:0,

                    }
                }}
            >
                {hasInvoicePermission && props.orderData.state === general_states.SHIPPED &&
                <ActionButton
                    onClick={() => props.addInvoiceProductModal.setOpen(true)}
                >
                    <ActionIcon>
                        <AddShoppingCartIcon fontSize="small"/>
                    </ActionIcon>

                    <Box>
                        <Text>
                        {t('add_invoice_product')}
                        </Text>
                    </Box>
                </ActionButton>
                }
                {hasInvoicePermission && props.orderData.state === general_states.SHIPPED && !props.orderData.withoutCosts &&
                <ActionButton
                    onClick={() => props.confirmNoCostOrderModal.setOpen(true)}
                >
                    <ActionIcon>
                        <ClearIcon fontSize="small"/>
                    </ActionIcon>

                    <Box>
                        <Text>
                        {t('order_no_costs')}
                        </Text>
                    </Box>
                </ActionButton>
                }
                {props.calculateOrderCosts &&
                    <ActionButton
                        onClick={() => props.calculateOrderCosts()}
                    >
                        <Box>
                            <Text>
                            {t("recalculate_invoice_product")}
                            </Text>
                        </Box>
                    </ActionButton>
                }
                {props.orderData && ![general_states.SHIPPED, general_states.FULLYPICKED, general_states.PICKING_IN_PROGRESS, general_states.CANCELLED].includes(props.orderData?.state) &&
                     props.orderData.amountOfProducts > 1 && hasModifyPrivilege &&
                    <ActionButton
                        onClick={() => props.addModal.setOpen(true)}
                    >
                        <ActionIcon>
                            <SplitscreenIcon fontSize="small"/>
                        </ActionIcon>
                        <Box>
                            <Text>
                            {t('split_order')}
                            </Text>
                        </Box>
                    </ActionButton>
                }
                {hasModifyPrivilege &&
                    <ActionButton
                        onClick={() => props.confirmDuplicateModal.setOpen(true)}
                    >
                        <ActionIcon>
                            <ContentCopyIcon fontSize="small"/>
                        </ActionIcon>

                        <Box>
                            <Text>
                            {t('duplicate_order')}
                            </Text>
                        </Box>
                    </ActionButton>
                }
                {hasCreateReturnPermission && props.orderData?.state === general_states.SHIPPED && 
                    <ActionButton
                        onClick={() => props.returnModal.setOpen(true)}
                    >
                        <ActionIcon>
                            <ReturnPackage fontSize="small"/>
                        </ActionIcon>

                        <Box>
                            <Text>
                            {t('create_return')}
                            </Text>
                        </Box>
                    </ActionButton>
                }
                {canCreateClaim && props.claimModal &&
                    <ActionButton
                        onClick={() => props.claimModal.setOpen(true)}
                    >
                        <ActionIcon>
                            <ReportProblemOutlinedIcon fontSize="small"/>
                        </ActionIcon>

                        <Box>
                            <Text>
                            {t('create_claim')}
                            </Text>
                        </Box>
                    </ActionButton>
                }
                {hasManageShipmentPermission && ![general_states.CANCELLED, general_states.BACKORDERED, general_states.SNOOZE, general_states.REPLENISH].includes(props.orderData?.state) &&
                    <ActionButton
                        onClick={() => {
                            // Dropship orders require the operator to confirm the order's identity by
                            // typing the last 4 characters of its webshop reference before shipping.
                            // Users with the order_dropship_skipcheck privilege go straight to the shipment page.
                            if(props.orderData?.webshopcredentials?.dropshipOrders && props.dropshipVerifyModal && !hasDropshipSkipCheckPermission){
                                props.dropshipVerifyModal.request(proceedToShipment);
                            } else {
                                proceedToShipment();
                            }
                        }}
                    >
                        <ActionIcon>
                            <Truck fontSize="small"/>
                        </ActionIcon>

                        <Box>
                            <Text>
                            {t('create_shipment')}
                            </Text>
                        </Box>
                    </ActionButton>
                }
                {canEditPickup && props.orderData?.deliveryMethod !== "pickup" &&
                <ActionButton
                        onClick={() => props.confirmPickupModal.setOpen(true)}
                    >
                    <ActionIcon>
                        <WarehouseIcon fontSize="small"/>
                    </ActionIcon>
                        <Box>
                            <Text>
                            {t('order_to_pickup')}
                            </Text>
                        </Box>
                    </ActionButton>
                }
                {canEditPickup && props.orderData?.deliveryMethod === "pickup" &&
                    <ActionButton
                        onClick={() => props.confirmShipmentModal.setOpen(true)}
                    >
                        <ActionIcon>
                            <LocalShippingIcon fontSize="small"/>
                        </ActionIcon>
                        <Box>
                            <Text>
                            {t('order_to_ship')}
                            </Text>
                        </Box>
                    </ActionButton>
                }
                {canEditPickingType &&
                    <ActionButton
                        onClick={() => props.confirmPickFromBulkModal.setOpen(true)}
                    >
                        <ActionIcon>
                            <LocalShippingIcon fontSize="small"/>
                        </ActionIcon>
                        <Box>
                            <Text>
                            {t('pick_from_bulk')}
                            </Text>
                        </Box>
                    </ActionButton>
                }
                {hasModifyPrivilege && props.orderData?.state && ![general_states.SHIPPED, general_states.CANCELLED, general_states.ECHECK, general_states.FULLYPICKED].includes(props.orderData.state) &&
                
                    <ActionButton
                        onClick={() => props.orderToEditModal.setOpen(true)}
                    >
                        <ActionIcon>
                            <EditIcon fontSize="small"/>
                        </ActionIcon>
                        <Box>
                            <Text>
                            {t('edit_order')}
                            </Text>
                        </Box>
                    </ActionButton>
                }
                
                {hasSetToShippedPermission && props.orderData?.state && ![general_states.SHIPPED, general_states.BACKORDERED, general_states.CANCELLED].includes(props.orderData?.state) && 
                <ActionButton
                    onClick={() => props.setToShippedModal.setOpen(true)}
                    >
                    <ActionIcon>
                        <LocalShippingIcon fontSize="small"/>
                    </ActionIcon>
                        <Box>
                            <Text>
                            {t('set_order_to_shipped')}
                            </Text>
                        </Box>
                    </ActionButton>}
                {hasModifyPrivilege && [general_states.BACKORDERED].includes(props.orderData?.state) && 
                <ActionButton
                    onClick={() => props.priorityModal.setOpen(true)}
                    >
                    <ActionIcon>
                        <EditIcon fontSize="small"/>
                    </ActionIcon>
                        <Box>
                            <Text>
                            {t('edit_priority')}
                            </Text>
                        </Box>
                    </ActionButton>}
                {hasModifyPrivilege && [general_states.CANCELLED, general_states.SHIPPED].includes(props.orderData?.state) && 
                <ActionButton
                    onClick={() => props.remarkModal.setOpen(true)}
                    >
                    <ActionIcon>
                        <EditIcon fontSize="small"/>
                    </ActionIcon>
                        <Box>
                            <Text>
                            {t('add_remark')}
                            </Text>
                        </Box>
                    </ActionButton>}
                {hasModifyPrivilege && !isFulfilment && props.eventModal &&
                <ActionButton
                    onClick={() => props.eventModal.setOpen(true)}
                    >
                    <ActionIcon>
                        <EventNoteOutlinedIcon fontSize="small"/>
                    </ActionIcon>
                        <Box>
                            <Text>
                            {t('add_event')}
                            </Text>
                        </Box>
                    </ActionButton>}
                {hasModifyPrivilege && props.orderData?.state && props.orderData?.webshopcredentials && !props.orderData.webshopcredentials.integratorId && props.orderData.webshopcredentials.webshop_type !== "api" && ![general_states.SHIPPED, general_states.CANCELLED, general_states.ECHECK, general_states.FULLYPICKED].includes(props.orderData.state) &&
                    <ActionButton
                        onClick={() => props.resyncOrderModal.setOpen(true)}
                    >
                        <ActionIcon>
                            <SyncIcon fontSize="small"/>
                        </ActionIcon>
                        <Box>
                            <Text>
                            {t('resync_order')}
                            </Text>
                        </Box>
                    </ActionButton>
                }
                {canViewWebshopJson &&
                    <ActionButton
                        onClick={() => props.webshopJsonModal.setOpen(true)}
                    >
                        <ActionIcon>
                            <DataObjectIcon fontSize="small"/>
                        </ActionIcon>
                        <Box>
                            <Text>
                            {t('webshop_json')}
                            </Text>
                        </Box>
                    </ActionButton>
                }
                {hasInvoicePermission && props.orderData?.state === general_states.SHIPPED &&
                    <ActionButton
                        onClick={() => props.checkModal.setOpen(true)}
                    >
                        <ActionIcon>
                            <FactCheckIcon fontSize="small"/>
                        </ActionIcon>
                        <Box>
                            <Text>
                            {t('check_one_order_costs')}
                            </Text>
                        </Box>
                    </ActionButton>
                }
                {hasInvoicePermission &&
                    <ActionButton
                        onClick={() => props.checkCostRuleModal.setOpen(true)}
                    >
                        <ActionIcon>
                            <RuleIcon fontSize="small"/>
                        </ActionIcon>
                        <Box>
                            <Text>
                            {t('check_cost_rule')}
                            </Text>
                        </Box>
                    </ActionButton>
                }
                <CreateSupportTicketActionButton
                    linkedItem={linkedItemFromOrder(props.orderData)}
                    relationId={props.orderData?.relationId}
                    onClose={props.onClose}
                />
            </Box>
            <MSelectWorkspaces
                open={workSpaceModal.open} 
                handleClose={() => workSpaceModal.setOpen(false)}
            />

        </PopoverModal>
    );
}

export default AMOrderSpWarehouses;


export const AMOrderSpRelations = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const colors = tokens(theme.palette.mode);
    const language = useSelector((state) => state.settings.language);
    const themeMode = useSelector((state) => state.settings.themeMode);
    const dispatch = useDispatch();
    const hasCreateReturnPermission = useSelector((state) => 
        selectUserPermission(state, [UserPrivileges.RP_RETURN_MANAGE])
    );

    const hasModifyPrivilege = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.RP_ORDER_MANAGE])
    );

    const {data:orderHash, error:hashError, isLoading:hashLoading, isFetching:hashFetching, isSuccess:hashSuccess, isError:hashIsError} = useFindUrlForReturnQuery(props.orderData?.id, {
        skip: !props.orderData?.id || props.orderData?.state !== general_states.SHIPPED
    })

    useEffect(() => {
        console.log('useEffect Order Hash Debug:', {
            orderHash,
            orderHashType: typeof orderHash,
            orderHashKeys: orderHash ? Object.keys(orderHash) : null,
            hashError,
            hashLoading,
            hashFetching,
            hashSuccess,
            hashIsError,
            orderId: props.orderData?.id,
            state: props.orderData?.state,
            isShipped: props.orderData?.state === general_states.SHIPPED
        });
    }, [orderHash])


    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    const canEditPickup = (!props.orderData?.webshopcredentials || (props.orderData?.webshopcredentials && props.orderData.webshopcredentials.webshop_type !== "bol" && 
    props.orderData.webshopcredentials.integratorId === null)) && hasModifyPrivilege &&
    ![general_states.SHIPPED, general_states.CANCELLED].includes(props.orderData?.state);

    // A claim needs a shipped order on a shipper that accepts them — the shipper's claim mailbox
    // is what the claim is actually sent to.
    const canCreateClaim = hasModifyPrivilege && props.orderData?.state === general_states.SHIPPED &&
    claimableShipments(props.orderData).some(shipment => shipment.shipper?.canCreateClaim);

    return (
        <PopoverModal
            {...props}
        >
            <Box 
                sx={{
                    background: theme.palette.mode === "dark" ? colors.grey[100] : "#fff",
                    padding:1.5,
                    paddingBottom: isStandAlone ? 4 : 1.5,
                    display:"flex",
                    gap:1,
                    flexDirection:"column",
                    borderRadius:"24px",
                    borderBottomLeftRadius:0,
                    borderBottomRightRadius:0,
                    "@media screen and (max-width: 48em)" : {
                        borderRadius:"32px",
                        borderBottomLeftRadius:0,
                        borderBottomRightRadius:0,

                    }
                }}
            >
                {canCreateClaim && props.claimModal &&
                    <ActionButton
                        onClick={() => props.claimModal.setOpen(true)}
                    >
                        <ActionIcon>
                            <ReportProblemOutlinedIcon fontSize="small"/>
                        </ActionIcon>

                        <Box>
                            <Text>
                            {t('create_claim')}
                            </Text>
                        </Box>
                    </ActionButton>
                }

                {props.orderData && ![general_states.SHIPPED, general_states.FULLYPICKED, general_states.PICKING_IN_PROGRESS, general_states.CANCELLED].includes(props.orderData?.state) &&
                    (props.orderData.amountOfProducts > 1 && hasModifyPrivilege) &&
                    <ActionButton
                        onClick={() => props.addModal.setOpen(true)}
                    >
                        <ActionIcon>
                            <SplitscreenIcon fontSize="small"/>
                        </ActionIcon>
                        <Box>
                            <Text>
                            {t('split_order')}
                            </Text>
                        </Box>
                    </ActionButton>
                }
                {hasModifyPrivilege &&
                    <ActionButton
                        onClick={() => props.confirmDuplicateModal.setOpen(true)}
                    >
                        <ActionIcon>
                            <ContentCopyIcon fontSize="small"/>
                        </ActionIcon>

                        <Box>
                            <Text>
                            {t('duplicate_order')}
                            </Text>
                        </Box>
                    </ActionButton>
                }
                {hasCreateReturnPermission && props.orderData?.state === general_states.SHIPPED && 
                    <ActionButton
                        onClick={() => {
                            props.returnModal.setOpen(true)
                        }}
                    >
                        <ActionIcon>
                            <ReturnPackage fontSize="small"/>
                        </ActionIcon>

                        <Box>
                            <Text>
                            {t('create_return')}
                            </Text>
                        </Box>
                    </ActionButton>
                }
                {canEditPickup && props.orderData?.deliveryMethod !== "pickup" &&
                <ActionButton
                        onClick={() => props.confirmPickupModal.setOpen(true)}
                    >
                    <ActionIcon>
                        <WarehouseIcon fontSize="small"/>
                    </ActionIcon>
                        <Box>
                            <Text>
                            {t('order_to_pickup')}
                            </Text>
                        </Box>
                    </ActionButton>
                }
                {canEditPickup && props.orderData?.deliveryMethod === "pickup" &&
                <ActionButton
                    onClick={() => props.confirmShipmentModal.setOpen(true)}
                    >
                    <ActionIcon>
                        <LocalShippingIcon fontSize="small"/>
                    </ActionIcon>
                        <Box>
                            <Text>
                            {t('order_to_ship')}
                            </Text>
                        </Box>
                    </ActionButton>
                }
                {hasModifyPrivilege && props.orderData?.state && ![general_states.SHIPPED, general_states.CANCELLED, general_states.ECHECK, general_states.FULLYPICKED].includes(props.orderData.state) &&
                    <ActionButton
                        onClick={() => props.orderToEditModal.setOpen(true)}
                    >
                        <ActionIcon>
                            <EditIcon fontSize="small"/>
                        </ActionIcon>
                        <Box>
                            <Text>
                            {t('edit_order')}
                            </Text>
                        </Box>
                    </ActionButton>
                }
                {hasModifyPrivilege && [general_states.BACKORDERED].includes(props.orderData?.state) && 
                <ActionButton
                    onClick={() => props.priorityModal.setOpen(true)}
                    >
                    <ActionIcon>
                        <EditIcon fontSize="small"/>
                    </ActionIcon>
                        <Box>
                            <Text>
                            {t('edit_priority')}
                            </Text>
                        </Box>
                    </ActionButton>}
                {hasModifyPrivilege &&
                <ActionButton
                    onClick={() => props.updateInternalRemarkModal.setOpen(true)}
                    >
                    <ActionIcon>
                        <EditIcon fontSize="small"/>
                    </ActionIcon>
                        <Box>
                            <Text>
                            {t('edit_internal_remark')}
                            </Text>
                        </Box>
                    </ActionButton>}
                {hasModifyPrivilege && props.eventModal &&
                <ActionButton
                    onClick={() => props.eventModal.setOpen(true)}
                    >
                    <ActionIcon>
                        <EventNoteOutlinedIcon fontSize="small"/>
                    </ActionIcon>
                        <Box>
                            <Text>
                            {t('add_event')}
                            </Text>
                        </Box>
                    </ActionButton>}
                {hasModifyPrivilege && props.orderData?.state === general_states.SHIPPED &&
                <ActionButton
                    onClick={() => props.freeReturnModal.setOpen(true)}
                    >
                    <ActionIcon>
                        <AssignmentReturnIcon fontSize="small"/>
                    </ActionIcon>
                        <Box>
                            <Text>
                            {props.orderData?.freeReturn ? t('paid_return') : t('free_return')}
                            </Text>
                        </Box>
                    </ActionButton>}
                {hasModifyPrivilege && props.orderData?.state && props.orderData?.webshopcredentials && !props.orderData.webshopcredentials.integratorId && props.orderData.webshopcredentials.webshop_type !== "api" && general_states.ACTION_REQUIRED_CUSTOMER === props.orderData.state &&
                    <ActionButton
                        onClick={() => props.resyncOrderModal.setOpen(true)}
                    >
                        <ActionIcon>
                            <SyncIcon fontSize="small"/>
                        </ActionIcon>
                        <Box>
                            <Text>
                            {t('resync_order')}
                            </Text>
                        </Box>
                    </ActionButton>
                }

                {props.orderData?.state === general_states.SHIPPED && orderHash?.hash &&
                    <Box
                        sx={{
                            borderRadius:3,
                            height:40,
                            // background:"orange",
                            background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                            // border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                            // boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                            width:1,
                            flex:1,
                            lineHeight:"40px",
                            display:'flex',
                            flexDirection:"column",
                            maxWidth:450,
                        }}
                    >
                        <Box
                            sx={{
                                // maxWidth:300,
                                whiteSpace:"nowrap",
                                wordBreak:"keep-all",
                                textOverflow:"ellipsis",
                                overflow:"hidden",
                                fontWeight:500,
                                fontFamily:"Roboto, sans-serif",
                                fontSize:14,
                                px:2,

                            }}
                        >
                            
                            {/* <Text bold> */}
                                {"https://www.clicktoreturn.com/"+orderHash.hash+'/order'}
                            {/* </Text> */}
                        </Box>
                        <ShCopyButtonWithOwnChildren
                            value={"https://www.clicktoreturn.com/"+orderHash.hash+"/order"}

                            sx={{
                                flex:1,
                                alignItems:"center",
                                pr:0.5,
                                flexShrink:0,
                            }}
                        >
                            <ButtonBase
                                sx={{
                                    height:32,
                                    borderRadius:3,
                                    px:2,
                                    display:"flex",
                                    alignItems:"center",
                                    justifyContent:'center',
                                    background: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                                    color: theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                    fontSize:14,
                                    fontWeight:700,
                                    fontFamily:"Roboto, sans-serif",
                                    width:1,
                                    borderTopLeftRadius:0,
                                    borderTopRightRadius:0,
                                }}
                            >
                                {t("copy_link")}
                            </ButtonBase>
                        </ShCopyButtonWithOwnChildren>
                    </Box>
                }
                <CreateSupportTicketActionButton
                    linkedItem={linkedItemFromOrder(props.orderData)}
                    relationId={props.orderData?.relationId}
                    onClose={props.onClose}
                />
            </Box>
        </PopoverModal>
    );
}
// {orderData && ![general_states.SHIPPED, general_states.FULLYPICKED, general_states.PICKING_IN_PROGRESS, general_states.CANCELLED].includes(orderData?.state) &&
//     (orderData.amountOfProducts > 1 && hasModifyPrivilege) &&
//     <Shbutton
//         block
//         onClick={() => addModal.setOpen(true)}
//         className={"group-btn"}
//         // onClick={props.action}
//         color={"primaryGrey"}
//         variant="contained"
//         // endIcon={<SaveIcon />}
//     >
//         {t('split_order')}
//         </Shbutton>
// }
// {hasModifyPrivilege &&
// <Shbutton
//     block
//     onClick={() => confirmDuplicateModal.setOpen(true)}
//     className={"group-btn"}
//     color={"primary"}
//     variant="contained"
//     endIcon={<ContentCopyIcon />}
// >
//     {t('duplicate_order')}
// </Shbutton>
// }
// {hasCreateReturnPermission && orderData?.state === general_states.SHIPPED && 
//     <Shbutton
//         block
//         onClick={() => returnModal.setOpen(true)}
//         className={"group-btn"}
//         // onClick={props.action}
//         color={"primaryGrey"}
//         variant="contained"
//         // endIcon={<SaveIcon />}
//     >
//         {t('create_return')}
//     </Shbutton>
// }
// {hasCreateReturnPermission && orderData?.state !== general_states.CANCELLED && orderData?.state !== general_states.SHIPPED && 
// <Shbutton
//     block
//     onClick={() => shipmentModal.setOpen(true)}
//     className={"group-btn"}
//     color={"primary"}
//     variant="contained"
// >
//     {t('create_shipment')}
// </Shbutton>
// }